import type { RealtimeChannel, SupabaseClient } from "@supabase/supabase-js";
import Debug from "debug";
import PQueue from "p-queue";
import { wireSupabaseRealtimeAuth, applySupabaseRealtimeAuth } from "./wireSupabaseRealtimeAuth";

const debug = Debug("oom:AccountBroadcastManager");

export type AccountBroadcastOperation = "INSERT" | "UPDATE" | "DELETE";

export interface AccountBroadcastPayload {
  change_log_id: string;
  table_name: string;
  row_id: string;
  operation: AccountBroadcastOperation;
  is_deleted: boolean;
  device_token: string | null;
  origin_token?: string | null;
}

export type AccountBroadcastHandler = (
  payload: AccountBroadcastPayload,
  event: AccountBroadcastOperation,
) => void;

const BROADCAST_EVENTS: AccountBroadcastOperation[] = ["INSERT", "UPDATE", "DELETE"];

/** Fail subscribe attempts that never reach SUBSCRIBED (e.g. missing Realtime JWT on Tauri). */
const SUBSCRIBE_TIMEOUT_MS = 30_000;
const INITIAL_SUBSCRIBE_RETRY_MS = 250;
const MAX_SUBSCRIBE_RETRY_MS = 5_000;

const TERMINAL_SUBSCRIBE_STATUSES = new Set(["CHANNEL_ERROR", "TIMED_OUT", "CLOSED"]);

/**
 * Subscribes to one stable private Realtime broadcast channel per account.
 * Topic: `account:<account_id>`.
 */
export class AccountBroadcastManager {
  private channel: RealtimeChannel | undefined;
  private accountId: string | null = null;
  private active = false;
  private subscribedToChannel = false;
  private handlers = new Set<AccountBroadcastHandler>();
  private _subscribeQueue = new PQueue({ concurrency: 1 });
  private _lastAuthResubscribeToken: string | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | undefined;
  private retryAttempt = 0;

  constructor(
    private supabase: SupabaseClient,
    private onSubscribedChange?: (subscribed: boolean) => void,
  ) {
    wireSupabaseRealtimeAuth(supabase, () => {
      if (!this.active || !this.accountId || !this.channel) return;
      void this._maybeResubscribeOnAuthChange();
    });
  }

  private _notifySubscribedChange() {
    this.onSubscribedChange?.(this.subscribed);
  }

  private async _maybeResubscribeOnAuthChange() {
    const { data } = await this.supabase.auth.getSession();
    const token = data.session?.access_token ?? null;
    if (token === this._lastAuthResubscribeToken) {
      return;
    }
    this._lastAuthResubscribeToken = token;
    if (this.subscribedToChannel && token) {
      return;
    }
    void this._queueResubscribe();
  }

  static topic(accountId: string) {
    return `account:${accountId}`;
  }

  get currentAccountId() {
    return this.accountId;
  }

  get subscribed() {
    return this.active && this.subscribedToChannel;
  }

  addHandler(handler: AccountBroadcastHandler) {
    this.handlers.add(handler);
    return () => {
      this.handlers.delete(handler);
    };
  }

  async setAccountId(accountId: string | null) {
    if (this.accountId === accountId) {
      return;
    }

    debug("setAccountId", { from: this.accountId, to: accountId });
    this._clearRetry();
    this.retryAttempt = 0;
    this.accountId = accountId;

    if (!accountId) {
      this.active = false;
      this._notifySubscribedChange();
      return this._queueResubscribe();
    }

    if (this.active) {
      return this._queueResubscribe();
    }
  }

  async ensureSubscribed() {
    if (!this.accountId) {
      debug("ensureSubscribed skipped: no account id");
      return;
    }

    this.active = true;
    return this._queueResubscribe();
  }

  async unsubscribe() {
    this._clearRetry();
    this._subscribeQueue.clear();
    this.active = false;
    this.accountId = null;
    await this._unsubscribeCurrentChannel();
  }

  private _queueResubscribe(): Promise<void> {
    this._clearRetry();
    return this._subscribeQueue.add(async () => {
      await this._resubscribe();
    });
  }

  private _queueRetry(accountId: string) {
    if (!this.active || this.accountId !== accountId || this.retryTimer) {
      return;
    }

    const delayMs = Math.min(INITIAL_SUBSCRIBE_RETRY_MS * 2 ** this.retryAttempt, MAX_SUBSCRIBE_RETRY_MS);
    this.retryAttempt += 1;
    debug(`Retrying account broadcast subscription for ${accountId} in ${delayMs}ms`);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = undefined;
      if (!this.active || this.accountId !== accountId) {
        return;
      }
      void this._queueResubscribe();
    }, delayMs);
  }

  private _clearRetry() {
    if (!this.retryTimer) {
      return;
    }
    clearTimeout(this.retryTimer);
    this.retryTimer = undefined;
  }

  private async _resubscribe() {
    const accountId = this.accountId;
    const shouldSubscribe = this.active && accountId !== null;

    await this._unsubscribeCurrentChannel();

    if (!shouldSubscribe || !accountId) {
      debug("No active account broadcast subscription");
      this._notifySubscribedChange();
      return;
    }

    const topic = AccountBroadcastManager.topic(accountId);
    debug(`Subscribing to account broadcast channel: ${topic}`);

    try {
      this._lastAuthResubscribeToken = await applySupabaseRealtimeAuth(this.supabase);
      const channel = this.supabase.channel(topic, { config: { private: true } });
      this.channel = channel;

      for (const event of BROADCAST_EVENTS) {
        channel.on("broadcast", { event }, (message) => {
          const payload = message.payload as AccountBroadcastPayload | undefined;
          if (!payload) {
            debug(`Ignoring broadcast without payload on ${topic}`, message);
            return;
          }
          this._dispatch(payload, event);
        });
      }

      await new Promise<void>((resolve, reject) => {
        const isStale = () => !this.active || this.accountId !== accountId || this.channel !== channel;
        const timeout = setTimeout(() => {
          if (isStale()) {
            resolve();
            return;
          }
          reject(new Error(`Account broadcast subscription timed out after ${SUBSCRIBE_TIMEOUT_MS}ms`));
        }, SUBSCRIBE_TIMEOUT_MS);

        channel.subscribe((status, err) => {
          if (isStale()) {
            clearTimeout(timeout);
            resolve();
            return;
          }

          if (err) {
            clearTimeout(timeout);
            console.error(`[${topic}] Account broadcast subscription error:`, err);
            debug(`Error subscribing to ${topic}`, err);
            reject(err);
            return;
          }

          if (status === "SUBSCRIBED") {
            clearTimeout(timeout);
            debug(`Subscribed to account broadcast channel ${topic}`);
            this.subscribedToChannel = true;
            this.retryAttempt = 0;
            this._notifySubscribedChange();
            resolve();
            return;
          }

          if (TERMINAL_SUBSCRIBE_STATUSES.has(status)) {
            clearTimeout(timeout);
            reject(new Error(`Account broadcast subscription failed: ${status}`));
            return;
          }

          debug(`Account broadcast channel ${topic} status: ${status}`);
        });
      });
    } catch (error) {
      console.error(`[${topic}] Error during account broadcast subscription:`, error);
      debug("Error in account broadcast subscribe queue", error);
      await this._unsubscribeCurrentChannel();
      this._notifySubscribedChange();
      this._queueRetry(accountId);
    }
  }

  private _dispatch(payload: AccountBroadcastPayload, event: AccountBroadcastOperation) {
    debug("broadcast", event, payload);
    for (const handler of this.handlers) {
      try {
        handler(payload, event);
      } catch (error) {
        console.error("Error in account broadcast handler:", error);
        debug("Error in account broadcast handler", error);
      }
    }
  }

  private async _unsubscribeCurrentChannel() {
    const currentChannel = this.channel;
    if (!currentChannel) {
      return;
    }

    debug(`Unsubscribing from account broadcast channel ${currentChannel.topic}`);
    this.channel = undefined;
    this.subscribedToChannel = false;

    try {
      await currentChannel.unsubscribe();
      await this.supabase.removeChannel(currentChannel);
      debug(`Removed account broadcast channel ${currentChannel.topic}`);
    } catch (error) {
      console.error(`Error unsubscribing from account broadcast channel ${currentChannel.topic}:`, error);
      debug("Error unsubscribing from account broadcast channel", error);
    }

    this._notifySubscribedChange();
  }
}
